import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import OrderCard from "./OrderCard";

interface StationColumnProps {
  station: any;
  orders: any[];
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
}

export default function StationColumn({
  station,
  orders,
  canMoveLeft,
  canMoveRight,
  onMoveLeft,
  onMoveRight,
}: StationColumnProps) {
  const stationOrders = orders.filter((o) =>
    o.items?.some(
      (i: any) => i.stationId === station.id && i.status !== "SERVED" && i.status !== "CANCELLED",
    ),
  );

  // 2026-05-24 — Quando le comande della stazione eccedono il viewport
  // della colonna (cella della grid), il body scrolla internamente. Due
  // bottoni pulsanti (top + bottom) segnalano al chef contenuto fuori
  // dal fold. Lo schermo del cuoco non supporta touch-scroll, quindi i
  // bottoni sono l'UNICO modo per scorrere — devono essere entrambi
  // visibili e ben evidenti.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [showScrollUp, setShowScrollUp] = useState(false);

  const recomputeScrollHints = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const scrollable = el.scrollHeight > el.clientHeight + 2;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    const atTop = el.scrollTop < 8;
    setShowScrollDown(scrollable && !atBottom);
    setShowScrollUp(scrollable && !atTop);
  }, []);

  const handleScrollDownClick = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    // Scroll di una "page" alla volta (90% dell'altezza visibile) — più
    // utile di salto-fino-al-fondo quando in colonna ci sono più ordini
    // e il chef deve leggerli in sequenza.
    el.scrollBy({ top: el.clientHeight * 0.9, behavior: "smooth" });
  }, []);

  const handleScrollUpClick = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollBy({ top: -el.clientHeight * 0.9, behavior: "smooth" });
  }, []);

  // Recompute quando cambiano gli ordini O quando il body cambia dimensione
  // (resize finestra / zoom / font load). ResizeObserver e' robusto + non
  // genera loop perche' non leggiamo dimensioni nello stesso frame.
  useEffect(() => {
    recomputeScrollHints();
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => recomputeScrollHints());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recomputeScrollHints, stationOrders.length]);

  return (
    // 2026-05-24 — h-full + overflow-hidden + relative: il body interno
    // gestisce il proprio scroll, e il bottone "Altri piatti" si ancora
    // assoluto in basso alla colonna (sopra lo scroll body) cosi' resta
    // sempre visibile finche' c'e' contenuto sotto il fold.
    <div className="flex flex-col h-full overflow-hidden relative">
      {/* Station header */}
      <div
        className="flex items-center justify-between px-3 py-3 rounded-t-xl flex-shrink-0"
        style={{ backgroundColor: station.color || "#4361EE" }}
      >
        {onMoveLeft && (
          <button
            onClick={onMoveLeft}
            disabled={!canMoveLeft}
            className="p-1 rounded hover:bg-white/20 transition-colors disabled:opacity-20"
            title="Sposta a sinistra"
          >
            <ChevronLeft size={16} className="text-white" />
          </button>
        )}
        <div className="flex items-center gap-2 flex-1 justify-center">
          <h2 className="text-lg font-bold text-white">{station.name}</h2>
          <span className="bg-white/20 text-white text-sm px-2 py-0.5 rounded-full">
            {stationOrders.length}
          </span>
        </div>
        {onMoveRight && (
          <button
            onClick={onMoveRight}
            disabled={!canMoveRight}
            className="p-1 rounded hover:bg-white/20 transition-colors disabled:opacity-20"
            title="Sposta a destra"
          >
            <ChevronRight size={16} className="text-white" />
          </button>
        )}
      </div>

      {/* Orders — flex-1 + overflow-y-auto + min-h-0 = la colonna scrolla
          quando le comande sono troppo lunghe per il viewport. min-h-0 e'
          obbligatorio perche' il padre e' flex-col (senza, il browser
          rispetta min-content e ignora overflow). */}
      <div
        ref={bodyRef}
        onScroll={recomputeScrollHints}
        className="bg-kds-bg/50 rounded-b-xl p-2 space-y-3 flex-1 min-h-0 overflow-y-auto"
      >
        {stationOrders.length > 0 ? (
          stationOrders.map((order) => (
            <OrderCard key={order.id} order={order} compact stationId={station.id} />
          ))
        ) : (
          <div className="flex items-center justify-center h-32 text-kds-muted">
            Nessun ordine
          </div>
        )}
      </div>

      {/* 2026-05-24 — Bottoni pulsanti scroll (UP + DOWN), visible solo se
          il body ha contenuto fuori dal fold nella relativa direzione. Lo
          schermo del cuoco e' touch-disabled per lo scroll: i bottoni sono
          l'UNICO modo per scorrere. Pointer-events-none sul wrapper / -auto
          sulle pillole per non bloccare interazioni dietro. */}
      {showScrollUp && (
        <div
          className="pointer-events-none absolute inset-x-0 flex justify-center pt-2 z-10"
          // ~52px = header station (px-3 py-3 + line-height del titolo)
          style={{ top: "3.25rem" }}
          aria-live="polite"
        >
          <button
            type="button"
            onClick={handleScrollUpClick}
            className="pointer-events-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-kds-preparing text-white text-sm font-bold shadow-lg shadow-kds-preparing/50 ring-2 ring-kds-preparing/40 animate-bounce hover:brightness-110 transition-all"
            aria-label="Scorri verso l'alto per tornare ai piatti precedenti"
            title="Piatti sopra — tocca per scorrere su"
          >
            <ChevronUp size={18} />
            Piatti sopra
          </button>
        </div>
      )}
      {showScrollDown && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-2 z-10"
          aria-live="polite"
        >
          <button
            type="button"
            onClick={handleScrollDownClick}
            className="pointer-events-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-kds-preparing text-white text-sm font-bold shadow-lg shadow-kds-preparing/50 ring-2 ring-kds-preparing/40 animate-bounce hover:brightness-110 transition-all"
            aria-label="Scorri verso il basso per vedere gli altri piatti della stazione"
            title="Altri piatti sotto — tocca per scorrere"
          >
            <ChevronDown size={18} />
            Altri piatti
          </button>
        </div>
      )}
    </div>
  );
}
